import { z } from 'zod';
import { type Actor, actorSchema } from './actor';

/**
 * The contract of the record write path: what callers send, what comes back, and
 * what the event log records. In `shared` because the API layer, the MCP
 * dispatcher and the web client all speak it, while the implementation stays
 * behind `packages/server/src/records/` (INV-1).
 *
 * Values are keyed by FIELD METADATA UUID, never by name (INV-7). A rename must
 * not break a stored workflow, an import mapping or an open form.
 */

/** One incoming value per field id. `null` clears the field. */
export type RecordValueInput = Readonly<Record<string, unknown>>;

export interface RecordWriteRequest {
  readonly objectId: string;
  readonly values: RecordValueInput;
  readonly actor: Actor;
}

/**
 * INV-5: a per-field human lock. `userId` is null when a MANUAL write carried no
 * user (a console operation), because the lock still has to exist - it is the
 * value that is protected, not the person.
 */
export interface ProvenanceEntry {
  readonly userId: string | null;
  /** ISO-8601 timestamp of the human edit that set or refreshed the lock. */
  readonly at: string;
}

/**
 * Keyed by FIELD ID.
 *
 * DEVIATION from docs/decisions/provenance.md, which sketches
 * `{ fieldName: { userId, at } }`: INV-7 forbids keying anything on a mutable
 * name. A name-keyed lock silently disappears the moment the field is renamed -
 * and the next automated write then overwrites the human value, which is the one
 * outcome this mechanism exists to prevent. The semantics of the decision (no
 * expiry, clearing counts, approved-AI stays machine) are unchanged.
 */
export type ProvenanceMap = Readonly<Record<string, ProvenanceEntry>>;

export const provenanceEntrySchema = z.strictObject({
  userId: z.uuid().nullable(),
  at: z.iso.datetime({ offset: true }),
});

export const provenanceMapSchema = z.record(z.uuid(), provenanceEntrySchema);

/**
 * Event vocabulary (INV-4). `erased` is the tombstone erasure leaves behind;
 * `write_blocked` is an automated write that hit a human lock; `lock_released` is
 * a human handing a field back to the machines; `proposal_expired` is a pending
 * change (INV-10) that nobody decided within its window.
 *
 * `proposal_expired` only exists for proposals against an EXISTING record - an
 * expired `create` proposal has no record whose timeline could carry it, and
 * `record_event.record_id` is not nullable. Those are logged and notified instead;
 * the `meta.pending_change` row is their permanent record either way.
 */
export const RECORD_EVENT_ACTIONS = [
  'created',
  'updated',
  'soft_deleted',
  'restored',
  'erased',
  'write_blocked',
  'lock_released',
  'proposal_expired',
  /**
   * identity-resolution.md 4.5. `merged_from` on the WINNER carries the diffs the
   * merge applied plus the loser id; `merged_into` on the LOSER names the winner and
   * REPLACES the `soft_deleted` event for that operation (one operation, one event);
   * `unmerged` lands on both when a merge is undone.
   *
   * `merge_repointed` is the aggregate that replaces per-row `updated` events on a
   * merge above the fan-out threshold (4.4 step 3). Named for the merge rather than
   * for the bulk, because its payload is merge-shaped and a generic name would invite
   * a future CSV import to reuse an action whose consumers expect that payload.
   */
  'merged_from',
  'merged_into',
  'unmerged',
  'merge_repointed',
  /**
   * notes-tasks-comments.md 3.2 (D3): the ELEVEN annotation actions, emitted by the
   * annotations module on the PARENT record (INV-4's second emitter, A1).
   *
   * `task_completed` / `task_reopened` are their own actions rather than `task_edited`
   * with a diff, because completion is THE automation trigger and a subscription selects
   * by ACTION - a consumer cannot filter on the content of a diff. They ship as a PAIR
   * for the reason `unmerged` had to ship with `merged_into`: a consumer that acts on
   * completion and never hears the reversal diverges permanently and silently.
   *
   * There is deliberately no `*_restored`: `deleted_at` exists for the erasure/audit
   * posture, not to offer undelete, and re-emitting `*_added` would make the log lie.
   */
  'note_added',
  'note_edited',
  'note_deleted',
  'task_added',
  'task_edited',
  'task_completed',
  'task_reopened',
  'task_deleted',
  'comment_added',
  'comment_edited',
  'comment_deleted',
] as const;

export type RecordEventAction = (typeof RECORD_EVENT_ACTIONS)[number];

/** The three annotation kinds, in the order they appear in the design document. */
export const ANNOTATION_KINDS = ['note', 'task', 'comment'] as const;
export type AnnotationKind = (typeof ANNOTATION_KINDS)[number];

/**
 * The eleven annotation actions, as a set, so a consumer can ask "is this one of them"
 * without re-listing them. Derived from `RECORD_EVENT_ACTIONS` rather than typed out a
 * second time: two lists that must agree are a list that eventually disagrees.
 */
export const ANNOTATION_EVENT_ACTIONS: readonly RecordEventAction[] = RECORD_EVENT_ACTIONS.filter(
  (action): action is RecordEventAction =>
    ANNOTATION_KINDS.some((kind) => action.startsWith(`${kind}_`)),
);

export function isAnnotationAction(action: string): boolean {
  return (ANNOTATION_EVENT_ACTIONS as readonly string[]).includes(action);
}

export const recordEventActionSchema = z.literal([...RECORD_EVENT_ACTIONS]);

/** Before/after values of one field, as stored in the event's `values` payload. */
export interface RecordFieldDiff {
  readonly before: unknown;
  readonly after: unknown;
}

/**
 * The merge-shaped half of a merge event's payload (identity-resolution.md 4.5, W4/W5).
 *
 * On `merged_from` it names the loser and summarises the repointing; on `merged_into`
 * it names the winner, which is what lets a consumer repoint its own stored references
 * (the reverse-lookup case HubSpot solves with `hs_merged_object_ids`). On
 * `merge_repointed` it is the whole point: that event is a RESYNC SIGNAL rather than a
 * diff.
 *
 * `fieldIds` on the aggregate carries the UNION of the repointed field ids and is
 * never empty. Modelled with empty field ids it would go SILENT for every subscription
 * that filters by field - subscribed to `updated` on field X, and no `updated`
 * arrives - which is the one outcome W5 says must never happen.
 */
export interface RecordEventMerge {
  readonly winnerId: string;
  readonly loserId: string;
  readonly repointedCount: number;
  readonly fieldIds?: readonly string[] | undefined;
  /** True when per-row events were replaced by aggregates (4.4 step 3). */
  readonly aggregated?: boolean | undefined;
  /**
   * Notes, tasks and comments the merge moved to the winner
   * (notes-tasks-comments.md section 7 point 3).
   *
   * Counted SEPARATELY from `repointedCount`, which is relation rows and decides the
   * per-row event fan-out. Annotations emit no per-row events - they have no timeline of
   * their own - so folding them into that number would change when child records get
   * aggregate events for a reason that has nothing to do with children.
   */
  readonly annotationCount?: number | undefined;
}

/**
 * The annotation-shaped half of an annotation event's payload
 * (notes-tasks-comments.md 3.3).
 *
 * A `values` sibling of `fields`, exactly like `merge`: the event log has no annotation
 * columns and gets none. `fields` stays EMPTY for every annotation action - annotations
 * have no field metadata ids, and inventing some would make them field-gateable, which
 * webhooks.md section 1 spent its argument closing for `soft_deleted`.
 *
 * Only the sub-keys that CHANGED are present, the same diff discipline records follow:
 * `*_added` carries `before: null` throughout, `*_deleted` carries the final state as
 * `before`. The full body rides in the diff - that is what makes the history of a note
 * reconstructible when the row holds only the latest text, and what the 20k body cap
 * exists to bound.
 */
export interface RecordEventAnnotation {
  readonly kind: AnnotationKind;
  /** The annotation row id, never the parent record id. */
  readonly id: string;
  readonly body?: RecordFieldDiff | undefined;
  readonly title?: RecordFieldDiff | undefined;
  readonly assignee?: RecordFieldDiff | undefined;
  readonly dueDate?: RecordFieldDiff | undefined;
  /**
   * Tasks only: `open_item` / `follow_up` (migration 0027). Named `taskKind` because `kind`
   * above already says which KIND OF ANNOTATION this is.
   */
  readonly taskKind?: RecordFieldDiff | undefined;
  /** Comments hosted on a note only. */
  readonly noteId?: string | undefined;
  /** Replies only, and always a TOP-LEVEL comment id (threaded-flat, 2.4). */
  readonly replyTo?: string | undefined;
}

export interface RecordEventValues {
  readonly fields: Readonly<Record<string, RecordFieldDiff>>;
  readonly merge?: RecordEventMerge | undefined;
  readonly annotation?: RecordEventAnnotation | undefined;
  /**
   * WHY THIS WRITE WAS MADE, in plain words, when its writer said so (redesign register A2:
   * "the correction writes under a named actor ("Data correction") with the reason").
   *
   * Present only on an `updated` or `write_blocked` event whose `PATCH` carried a `reason`;
   * every other event's payload is exactly what it was before this key existed. A sibling of
   * `fields` like `merge` and `annotation`, so the event log gains no column, and it lives in
   * `values` rather than on the skeleton because it is content: erasure nulls it with the
   * diffs it explains. The actor (INV-3) is untouched; who wrote is still the key's label.
   */
  readonly reason?: string | undefined;
}

/** The longest reason a write may carry (`RecordEventValues.reason`). */
export const RECORD_WRITE_REASON_MAX_LENGTH = 500;

/**
 * A write's reason as the API accepts it: trimmed, never blank, at most
 * `RECORD_WRITE_REASON_MAX_LENGTH` characters - the same ceiling as an actor's `context`.
 */
export const recordWriteReasonSchema = z.string().trim().min(1).max(RECORD_WRITE_REASON_MAX_LENGTH);

/** A field an automated write could not apply because a human owns it. */
export interface BlockedField {
  readonly fieldId: string;
  readonly attempted: unknown;
  readonly lock: ProvenanceEntry;
}

export interface RecordSystemFields {
  readonly id: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
  readonly createdBy: Actor | null;
  readonly updatedBy: Actor | null;
  readonly provenance: ProvenanceMap;
  readonly position: number | null;
  /**
   * roles-and-permissions.md D5: which LOGIN this row belongs to, which is what a role
   * scoped to `own` matches on. Null for every write that had no acting user - AGENT,
   * WORKFLOW, IMPORT and API - and a role may opt into seeing those through
   * `include_unassigned`.
   */
  readonly assignedUserId: string | null;
}

export interface RecordResult {
  readonly system: RecordSystemFields;
  /** Logical values by field id; composites are objects, not columns. */
  readonly values: Readonly<Record<string, unknown>>;
  /**
   * identity-resolution.md 4.6: set when the id the caller ASKED for was merged away
   * and this body is the winner's.
   *
   * A payload field rather than a 301, because API consumers handle a payload field
   * reliably and redirects unevenly, and n8n is a consumer. A silent substitution was
   * the alternative and is how an integration - or Oliver, which holds record ids in
   * conversation context - reports the wrong person's history.
   */
  readonly mergedInto?: string | undefined;
  /**
   * The reverse lookup (4.6): which ids this record now answers for. HubSpot solves
   * the same problem with `hs_merged_object_ids`. Without it an integration can only
   * ask "where did this id go", never "which ids does this record now answer for", and
   * reconciling a downstream system needs the second question.
   */
  readonly mergedFrom?: readonly string[] | undefined;
}

export interface RecordWriteResult {
  readonly record: RecordResult;
  /** Empty unless a lock blocked part of an automated write (INV-5). */
  readonly blocked: readonly BlockedField[];
  /** False when nothing changed: a zero-change update writes and emits nothing. */
  readonly changed: boolean;
  /** Id of the event this write emitted, when it emitted one. */
  readonly eventId?: string | undefined;
}

export const recordWriteRequestSchema = z.strictObject({
  objectId: z.uuid(),
  values: z.record(z.uuid(), z.unknown()),
  actor: actorSchema,
});
